import { addError } from '@/store/reducers/ToastStore';
import { ChatUIContext, NoraStatus } from '@/components/context/ChatUIContext';
import { ConsentModal } from '@/components/chat/ConsentModal';
import { CORPUS_QA, MULTI_TOOL, PAPER_FINDER } from 'configs/NoraVariants';
import { CorpusQAChat } from '@/components/chat/CorpusQAChat';
import { fetchThreadState } from '@/store/thunks/FetchThreads';
import { getChatAPI } from '@/api/ChatApi';
import { getCurrentThread, setCurrentThread } from '@/store/reducers/UIState';
import { getFileAPI } from '@/api/FileApi';
import { getLayoverLogger } from '@/logging/LayoverLogger';
import { getMessageFromApi, Message, NewMessage } from '@/models/Message';
import {
  getThreadById,
  getThreadLoadingState,
  LOADING_STATE,
  sendMessage,
} from '@/store/reducers/ChatStore';
import { getThreadFromApi, Thread } from '@/models/Thread';
import { getThreadPath, useNavigateToChatPage } from '@/components/shared';
import { LoadingSpinnerLogo } from '@/components/system/LoadingSpinnerLogo';
import { MultiToolChat } from '@/components/chat//MultitoolChat';
import { optAppConfig } from '@/utils/config';
import { PaperFinderChat } from '@/components/chat/PaperFinderChat';
import { processUIToolCalls } from '@/actions/ProcessUIToolCalls';
import { RootState } from '@/store/store';
import { SEND_MESSAGE_TRACE_KEY, useTraces } from '@/hooks/traceHooks';
import { SpansContext } from '@/components/context/SpansContext';
import { Suggestions } from '@/components/chat/Suggestions';
import { track } from '@/analytics/track';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  useInitializeStepProgress,
  useReloadOnNewMessage,
  useReloadOnNewWidget,
  useReloadOnUpdateWidget,
  useShouldShowQueryConsentModal,
  useUpdateStoreOnStepProgress,
} from '@/components/chat/chatThreadHooks';
import { User } from '@/models/User';

import { connect } from 'react-redux';
import {
  Fragment,
  MouseEvent,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { styled } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import invariant from 'invariant';

export interface ChatThreadProps {
  currentUser: User | null;
}

const ChatWidget = ({ currentUser }: ChatThreadProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const chatApi = getChatAPI();
  const fileApi = getFileAPI();
  // currentThread is just to get context of which thread is in focus,
  // threadFromStore is the actual thread object that will get notified of new messages
  const currentThread = useAppSelector(getCurrentThread);
  const threadFromStore = useAppSelector((state) =>
    getThreadById(state, currentThread?.id ?? null),
  );
  const threadLoadingState = useAppSelector((state) =>
    getThreadLoadingState(state, currentThread?.id ?? ''),
  );
  const {
    setNoraStateForThread,
    noraState,
    setSuggestedChatMessage,
    setInputValueAndSelection,
  } = useContext(ChatUIContext);

  const { endSpan, getOrCreateSpan, withSpanContext } =
    useContext(SpansContext);
  const { initializeSendMessageTraces } = useTraces();

  const navigateToChatPage = useNavigateToChatPage();

  const shouldShowConsentModal = useShouldShowQueryConsentModal();
  const [isConsentModalOpen, setIsConsentModalOpen] = useState(false);
  const [files, setFiles] = useState<File[]>([]);

  const handleShowSuggestion = useCallback(
    (event: MouseEvent<HTMLAnchorElement>, suggestion: string) => {
      event.preventDefault();
      setSuggestedChatMessage(suggestion, 0, suggestion.length);
    },
    [setSuggestedChatMessage],
  );
  const isMessageBarPending =
    !!currentThread?.id &&
    noraState[currentThread.id]?.status === NoraStatus.LOADING;

  const createThread = async (): Promise<Thread | null> => {
    const { data } = await chatApi.createThread();
    if (!data) {
      return null;
    }
    return getThreadFromApi(data.thread);
  };

  // user selects file
  const onFilesChanged = useCallback(
    (files: File[]) => setFiles(files),
    [setFiles],
  );

  const onSendMessage = useCallback(
    async (rawText: string | null) => {
      const sendMsgSpan = getOrCreateSpan({
        key: SEND_MESSAGE_TRACE_KEY,
      });
      withSpanContext(sendMsgSpan, async () => {
        if (!rawText && !files.length) {
          return;
        }

        let text = rawText || '';
        invariant(currentUser, 'currentUser should be defined');

        let thread = currentThread;
        let handlerProfile = null;
        if (!thread) {
          thread = await createThread();
          dispatch(setCurrentThread(thread));
          handlerProfile = localStorage.getItem('defaultHandlerProfile');
        }
        invariant(thread, 'thread should be defined');

        sendMsgSpan.setAttribute('threadId', thread.id);
        // once we have access  to a threadId, start tracking various events from when the message is sent
        initializeSendMessageTraces(thread.id);

        const attachmentUrls: string[] = await Promise.all(
          files.map((file: File) => fileApi.uploadFile(thread.id, file)),
        );
        if (attachmentUrls.length > 0) {
          text +=
            '\n\nAttachments\n\n' +
            attachmentUrls.map((url) => '• ' + url).join('\n');
          console.debug('Modifying text with attachments:', text);
        }

        const msg: NewMessage = {
          sender: currentUser,
          strippedText: text,
          structuredText: text,
          text,
          timestamp: Date.now(),
          threadId: thread.id,
          viewerReaction: null,
          hasToolCalls: false,
        };

        if (thread) {
          setNoraStateForThread(thread.id, { status: NoraStatus.LOADING });
        }
        try {
          dispatch(sendMessage({ message: msg, thread }));
          track('chat_thread__send_msg', {
            threadId: thread.id,
            message: text,
          });
          const { data } = await chatApi.sendMessage(msg, handlerProfile);

          // when a new thread is created, we redirect to the new thread page which triggers a fetch of the thread
          // that request will occasionally still be in flight after the message is sent and overwrites the chatStore with an empty thread
          // so we need to refetch the thread state here to ensure the thread is up to date with the latest message
          dispatch(fetchThreadState(thread.id));

          // Reset the nora state to IDLE after sending the message to unlock sending the next message
          setNoraStateForThread(thread.id, { status: NoraStatus.IDLE });

          if (data?.message !== null) {
            const { message: response, metadata } = data;
            const responseMessage: Message = getMessageFromApi(
              thread.id,
              response,
            );
            const tools = metadata?.tools;
            if (tools && tools.length > 0) {
              processUIToolCalls({
                currentThreadId: thread.id,
                tools: metadata.tools,
                dispatch,
              });
            }
            dispatch(sendMessage({ message: responseMessage, thread }));
          } else if (data) {
            // Do nothing; server is allowed to return null for data.message
          } else {
            throw new Error('No data returned from Nora API');
          }
        } catch (error: any) {
          getLayoverLogger().logError(`Error sending message ${error.stack}`, {
            message: msg,
            thread: thread,
            error: error,
          });
          dispatch(
            addError({ text: 'Error sending message. Please try again.' }),
          );
        }

        endSpan(SEND_MESSAGE_TRACE_KEY);
      });
    },
    [currentThread, files, dispatch, currentUser, chatApi, navigateToChatPage],
  );

  const onSubmitConsent = ({
    errorSavingConsent,
  }: {
    errorSavingConsent: boolean;
  }): void => {
    setIsConsentModalOpen(false);
    if (errorSavingConsent) {
      getLayoverLogger().logError(
        `Failed to save data contribution consent for ${currentUser}`,
      );
    }
  };

  const onSubmitQuery = useCallback(
    async (text: string): Promise<void> => {
      onSendMessage(text);
      if (shouldShowConsentModal) {
        setIsConsentModalOpen(true);
      }
    },
    [shouldShowConsentModal, onSendMessage],
  );

  // Subscribe to GraphQL events for thread, report errors to console
  const { error: newMessageError } = useReloadOnNewMessage(
    currentThread?.id ?? null,
  );
  const { error: updateWidgetError } = useReloadOnUpdateWidget(
    currentThread?.id ?? null,
  );
  const { error: newWidgetError } = useReloadOnNewWidget(
    currentThread?.id ?? null,
  );
  const { error: initializeStepProgressError } = useInitializeStepProgress(
    currentThread?.id ?? null,
  );
  const { error: updateStepProgressError } = useUpdateStoreOnStepProgress(
    currentThread?.id ?? null,
  );
  const gqlError =
    newMessageError ??
    updateWidgetError ??
    newWidgetError ??
    initializeStepProgressError ??
    updateStepProgressError;
  useEffect(() => {
    if (gqlError) {
      console.error('Error in subscription', gqlError);
    }
  }, [gqlError]);

  // only show loading spinner the first time a thread is fetched
  const isThreadLoading =
    !threadLoadingState?.isInitialized &&
    threadLoadingState?.loadingState === LOADING_STATE.LOADING;

  // render different chat experiences depending on if we are in the multi-tool or corpus-qa experience
  const renderChat = (): ReactNode => {
    const noraExperienceVariant = optAppConfig((_) => _.experience.variant);

    if (!threadFromStore) {
      getLayoverLogger().logError(
        `Current thread not found in ChatStore [threadId=${currentThread?.id}]`,
      );
      return null;
    }

    switch (noraExperienceVariant) {
      case MULTI_TOOL: {
        return (
          <MultiToolChat
            thread={threadFromStore}
            isMessageBarPending={isMessageBarPending}
            onSendMessage={onSubmitQuery}
            onFilesChanged={onFilesChanged}
          />
        );
      }
      case CORPUS_QA: {
        return <CorpusQAChat thread={threadFromStore} />;
      }
      case PAPER_FINDER: {
        return (
          <PaperFinderChat
            thread={threadFromStore}
            isMessageBarPending={isMessageBarPending}
            onSendMessage={onSubmitQuery}
          />
        );
      }

      default: {
        return (
          <MultiToolChat
            thread={threadFromStore}
            isMessageBarPending={isMessageBarPending}
            onSendMessage={onSubmitQuery}
            onFilesChanged={onFilesChanged}
          />
        );
      }
    }
  };

  // clear message bar when switching threads
  useEffect(() => {
    setInputValueAndSelection({ text: '' });
  }, [setInputValueAndSelection, currentThread]);

  // Store default handler profile from initial URL
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const profile = searchParams.get('profile');
    if (profile) {
      localStorage.setItem('defaultHandlerProfile', profile);
    } else {
      localStorage.removeItem('defaultHandlerProfile');
    }
  }, []);

  // Update the profile= URL parameter to match the current thread
  useEffect(() => {
    if (currentThread) {
      navigateToChatPage(currentThread);
      const showActiveProfile = async () => {
        const { data } = await chatApi.getThreadProfile(currentThread.id);
        const activeProfile = data.profile;
        const searchParams = new URLSearchParams(location.search);
        if (!searchParams.has('profile')) {
          searchParams.append('profile', activeProfile.name); // Append the parameter
          navigate(
            `${getThreadPath(currentThread)}?${searchParams.toString()}`,
            { replace: true },
          );
        }
      };
      showActiveProfile();
    }
  }, [currentThread]);

  return (
    <Fragment>
      {currentThread ? (
        <Fragment>
          {isThreadLoading ? <LoadingSpinnerLogo /> : renderChat()}
        </Fragment>
      ) : (
        <Suggestions
          handleShowSuggestion={handleShowSuggestion}
          handleSendMessage={onSubmitQuery}
          onFilesChanged={onFilesChanged}
          isMessageBarPending={isMessageBarPending}
        />
      )}
      {currentUser && isConsentModalOpen && (
        <ConsentModal onConsent={onSubmitConsent} currentUser={currentUser} />
      )}
    </Fragment>
  );
};

const mapStateToProps = (state: RootState): ChatThreadProps => {
  return {
    currentUser: state.userStore.currentUser,
  };
};

export const ChatThread = connect(mapStateToProps)(ChatWidget);

export const Scroller = styled('div')`
  inset: 0;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing(3)};
  padding-top: 0;
  position: absolute;

  /* Firefox */
  & {
    scrollbar-width: auto;
    scrollbar-color: #0a3235 #135458;
  }

  /* Chrome, Edge, and Safari */
  &::-webkit-scrollbar {
    width: 16px;
  }
  &::-webkit-scrollbar-track {
    background: #135458;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #0a3235;
    border-radius: 10px;
    border: 3px solid #ffffff;
  }
`;

export const MessageFadePadding = styled('div')`
  padding-top: ${({ theme }) => theme.spacing(6)};
`;
